import * as React from 'react';
import { useEffect, useState } from 'react';
import { Button, Spinner } from '@patternfly/react-core';
import { BoltIcon, CheckCircleIcon, MinusIcon, PlusIcon } from '@patternfly/react-icons';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import {
    createTopUpCheckout,
    generateIdempotencyKey,
    getSubscription,
    getTopUpCatalog
} from '@app/utils/utils';
import {
    ActivationError,
    SubscriptionResponse,
    TopUpCatalogResponse,
    TopUpCheckoutResponse,
    TopUpPackResponse
} from '@app/Subscription/types/subscription';
import {
    TopUpContext,
    TopUpSelection,
    computeTopUpTotal,
    defaultTopUpSelection,
    selectedTopUpItems,
    visibleTopUpPacks
} from '@app/Subscription/topUpUtils';
import { getStripe } from '@app/Subscription/SubscribePaymentModal';
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { colors, formResetStyle, radii } from '@app/components/designTokens';

function formatCents(cents: number): string {
    return '$' + (cents / 100).toFixed(2).replace(/\.00$/, '');
}

interface TopUpModalProps {
    isOpen: boolean;
    context: TopUpContext;
    onClose: () => void;
    /** Fired once the purchase is confirmed (with the freshest subscription we could fetch). */
    onPurchased?: (sub: SubscriptionResponse | null) => void;
}

type Step = 'pick' | 'pay' | 'finalizing' | 'done';

export const TopUpModal: React.FC<TopUpModalProps> = ({ isOpen, context, onClose, onPurchased }) => {
    const [step, setStep] = useState<Step>('pick');
    const [loading, setLoading] = useState(true);
    const [catalog, setCatalog] = useState<TopUpCatalogResponse | null>(null);
    const [subscription, setSubscription] = useState<SubscriptionResponse | null>(null);
    const [selection, setSelection] = useState<TopUpSelection[]>([]);
    const [checkout, setCheckout] = useState<TopUpCheckoutResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [needsPlan, setNeedsPlan] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    // True when the webhook hadn't landed by the end of the poll — credits arrive shortly.
    const [delayed, setDelayed] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        let cancelled = false;
        setStep('pick');
        setCheckout(null);
        setError(null);
        setNeedsPlan(false);
        setDelayed(false);
        setLoading(true);
        Promise.all([getTopUpCatalog(), getSubscription()])
            .then(([cat, sub]) => {
                if (cancelled) return;
                setCatalog(cat);
                setSubscription(sub);
                setSelection(defaultTopUpSelection(context, cat, sub));
            })
            .catch((err: any) => {
                if (cancelled) return;
                if (err instanceof ActivationError && err.body.reason === 'no_active_subscription') {
                    setNeedsPlan(true);
                } else {
                    setError('Unable to load credit packs. Please try again.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [isOpen, context]);

    if (!isOpen) return null;

    const packs = catalog ? visibleTopUpPacks(catalog.packs, subscription) : [];
    const totalCents = computeTopUpTotal(selection, packs);
    const anySelected = selection.some((s) => s.selected);

    const updateSelection = (sku: string, patch: Partial<TopUpSelection>) => {
        setSelection((prev) => prev.map((s) => (s.sku === sku ? { ...s, ...patch } : s)));
    };

    const handleContinue = async () => {
        setSubmitting(true);
        setError(null);
        try {
            const response = await createTopUpCheckout(selectedTopUpItems(selection), generateIdempotencyKey());
            setCheckout(response);
            setStep('pay');
        } catch (err: any) {
            if (err instanceof ActivationError && err.body.reason === 'no_active_subscription') {
                setNeedsPlan(true);
            } else {
                setError(err.message || 'Failed to start checkout.');
            }
        } finally {
            setSubmitting(false);
        }
    };

    // Payment confirmed client-side; credits are granted by the payment_intent.succeeded
    // webhook. Poll until a purchased balance rises above the pre-checkout snapshot.
    const handlePaymentConfirmed = async () => {
        setStep('finalizing');
        const before = {
            campaign: subscription?.credits?.purchased ?? 0,
            ai: subscription?.aiAds?.purchased ?? 0
        };
        const TIMEOUT_MS = 15000;
        const INTERVAL_MS = 1000;
        const start = Date.now();
        let latest: SubscriptionResponse | null = subscription;
        while (Date.now() - start < TIMEOUT_MS) {
            try {
                const sub = await getSubscription();
                if (sub) {
                    latest = sub;
                    const bumped =
                        (sub.credits?.purchased ?? 0) > before.campaign ||
                        (sub.aiAds?.purchased ?? 0) > before.ai;
                    if (bumped) {
                        setSubscription(sub);
                        setStep('done');
                        onPurchased?.(sub);
                        return;
                    }
                }
            } catch (err) {
                console.warn('poll /subscription failed:', err);
            }
            await new Promise((r) => setTimeout(r, INTERVAL_MS));
        }
        // Payment went through; the grant is just slow. Never show this as an error.
        if (latest) setSubscription(latest);
        setDelayed(true);
        setStep('done');
        onPurchased?.(latest);
    };

    const heading =
        step === 'done' ? 'Credits added' : step === 'finalizing' ? 'Adding your credits' : 'Buy credits';
    const subhead =
        step === 'pick'
            ? needsPlan
                ? 'One-off credit purchases'
                : 'One-time purchase — credits never expire with your billing period'
            : step === 'pay'
              ? 'Card details are processed securely by Stripe'
              : step === 'finalizing'
                ? 'Confirming your purchase — this only takes a moment…'
                : delayed
                  ? 'Payment received. Your credits will appear shortly.'
                  : 'Your new credits are ready to use.';

    return (
        <AppModal isOpen={isOpen} onClose={onClose} width="md" closeOnBackdropClick={step === 'pick'}>
            <div style={{ padding: '40px 32px 32px' }}>
                <div style={{ textAlign: 'center', marginBottom: '24px' }}>
                    <LogoBadge size={48}>
                        <BoltIcon style={{ fontSize: 24, color: colors.boltGold }} />
                    </LogoBadge>
                    <h1 style={{ color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' }}>
                        {heading}
                    </h1>
                    <p style={{ color: colors.subtle, fontSize: '14px', margin: 0 }}>{subhead}</p>
                </div>

                {step === 'pick' && (
                    <>
                        {loading ? (
                            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
                                <Spinner size="lg" aria-label="Loading credit packs" />
                            </div>
                        ) : needsPlan ? (
                            // Defensive: current backend spec allows top-ups without a plan, but an
                            // older backend may still 402 here.
                            <div style={{ textAlign: 'center', padding: '8px 0 4px' }}>
                                <p style={{ color: colors.body, fontSize: '14px', marginBottom: '16px' }}>
                                    Credit top-ups aren't quite ready yet — check back soon. In the meantime, a
                                    subscription is the fastest way to get credits.
                                </p>
                                <GradientButton onClick={onClose}>Close</GradientButton>
                            </div>
                        ) : (
                            <>
                                {packs.map((pack) => {
                                    const item = selection.find((s) => s.sku === pack.sku);
                                    if (!item) return null;
                                    return (
                                        <PackRow
                                            key={pack.sku}
                                            pack={pack}
                                            selected={item.selected}
                                            quantity={item.quantity}
                                            onToggle={() => updateSelection(pack.sku, { selected: !item.selected })}
                                            onQuantity={(q) => updateSelection(pack.sku, { quantity: q, selected: true })}
                                        />
                                    );
                                })}
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginTop: '16px',
                                    paddingTop: '14px',
                                    borderTop: `1px solid ${colors.border}`
                                }}>
                                    <span style={{ color: colors.subtle, fontSize: '14px', fontWeight: 600 }}>Total</span>
                                    <span style={{ color: colors.title, fontSize: '20px', fontWeight: 700 }}>
                                        {formatCents(totalCents)}
                                    </span>
                                </div>
                                {error && <ErrorBox message={error} />}
                                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                                    <Button variant="link" onClick={onClose} style={{ color: colors.subtle, fontWeight: 600 }}>
                                        Cancel
                                    </Button>
                                    <GradientButton
                                        isDisabled={!anySelected || submitting}
                                        isLoading={submitting}
                                        onClick={handleContinue}
                                    >
                                        {submitting ? 'Starting checkout…' : 'Continue to payment'}
                                    </GradientButton>
                                </div>
                            </>
                        )}
                    </>
                )}

                {step === 'pay' && checkout && (
                    <>
                        <div style={{
                            background: colors.bgMuted,
                            borderRadius: radii.input,
                            padding: '12px 14px',
                            marginBottom: '16px'
                        }}>
                            {checkout.lineItems.map((line) => {
                                const pack = packs.find((p) => p.sku === line.sku);
                                return (
                                    <div key={line.sku} style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: colors.body, padding: '2px 0' }}>
                                        <span>
                                            {pack ? pack.displayName : line.sku}
                                            {line.quantity > 1 ? ` × ${line.quantity}` : ''}
                                        </span>
                                        <span style={{ fontWeight: 600 }}>{formatCents(line.totalCents)}</span>
                                    </div>
                                );
                            })}
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: colors.title, fontWeight: 700, marginTop: '6px', paddingTop: '6px', borderTop: `1px solid ${colors.border}` }}>
                                <span>Total</span>
                                <span>{formatCents(checkout.amountCents)}</span>
                            </div>
                        </div>
                        <Elements stripe={getStripe()} options={{ clientSecret: checkout.clientSecret }}>
                            <TopUpPaymentForm
                                onConfirmed={handlePaymentConfirmed}
                                onBack={() => {
                                    setCheckout(null);
                                    setStep('pick');
                                }}
                            />
                        </Elements>
                    </>
                )}

                {step === 'finalizing' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '24px 0 8px' }}>
                        <Spinner size="lg" aria-label="Finalizing" />
                        <span style={{ color: colors.subtle, fontSize: '14px' }}>Adding credits to your account…</span>
                    </div>
                )}

                {step === 'done' && (
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '14px', padding: '12px 0 4px' }}>
                        <CheckCircleIcon style={{ fontSize: 40, color: colors.success }} />
                        {!delayed && subscription && (
                            <div style={{ textAlign: 'center', fontSize: '14px', color: colors.body }}>
                                {typeof subscription.credits?.purchased === 'number' && subscription.credits.purchased > 0 && (
                                    <div>{subscription.credits.purchased} extra campaign credits available</div>
                                )}
                                {typeof subscription.aiAds?.purchased === 'number' && subscription.aiAds.purchased > 0 && (
                                    <div>{subscription.aiAds.purchased} extra AI ad credits available</div>
                                )}
                            </div>
                        )}
                        <GradientButton onClick={onClose}>Done</GradientButton>
                    </div>
                )}
            </div>
        </AppModal>
    );
};

interface PackRowProps {
    pack: TopUpPackResponse;
    selected: boolean;
    quantity: number;
    onToggle: () => void;
    onQuantity: (q: number) => void;
}

const PackRow: React.FC<PackRowProps> = ({ pack, selected, quantity, onToggle, onQuantity }) => {
    const stepperButton: React.CSSProperties = {
        width: 26,
        height: 26,
        borderRadius: '50%',
        border: `1px solid ${colors.border}`,
        background: '#ffffff',
        color: colors.body,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontSize: 10,
        padding: 0
    };
    return (
        <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            padding: '12px 14px',
            marginBottom: '10px',
            border: `1px solid ${selected ? colors.accent : colors.border}`,
            boxShadow: selected ? `0 0 0 3px ${colors.accentRing}` : 'none',
            borderRadius: radii.input,
            background: '#ffffff',
            transition: 'border-color 0.15s, box-shadow 0.15s'
        }}>
            <input
                type="checkbox"
                checked={selected}
                onChange={onToggle}
                aria-label={`Include ${pack.displayName}`}
                style={{ width: 18, height: 18, accentColor: colors.accent, cursor: 'pointer', flexShrink: 0 }}
            />
            <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: colors.title, fontSize: '14px', fontWeight: 600 }}>{pack.displayName}</div>
                <div style={{ color: colors.subtle, fontSize: '12px' }}>
                    {formatCents(pack.priceCents)} per pack · {pack.quantity} {pack.creditType === 'AI_AD' ? 'AI ad' : 'campaign'} credits
                </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                <button type="button" style={stepperButton} aria-label="Decrease quantity" disabled={quantity <= 1} onClick={() => onQuantity(Math.max(1, quantity - 1))}>
                    <MinusIcon />
                </button>
                <span style={{ minWidth: 18, textAlign: 'center', fontSize: '14px', fontWeight: 600, color: colors.body }}>{quantity}</span>
                <button type="button" style={stepperButton} aria-label="Increase quantity" onClick={() => onQuantity(quantity + 1)}>
                    <PlusIcon />
                </button>
            </div>
            <div style={{ width: 62, textAlign: 'right', fontSize: '14px', fontWeight: 700, color: selected ? colors.title : colors.placeholder }}>
                {formatCents(pack.priceCents * quantity)}
            </div>
        </div>
    );
};

const ErrorBox: React.FC<{ message: string }> = ({ message }) => (
    <div style={{
        marginTop: '12px',
        color: colors.danger,
        fontSize: '14px',
        padding: '12px 14px',
        background: '#fef2f2',
        border: '1px solid #fecaca',
        borderRadius: radii.input
    }}>
        {message}
    </div>
);

interface TopUpPaymentFormProps {
    onConfirmed: () => void;
    onBack: () => void;
}

const TopUpPaymentForm: React.FC<TopUpPaymentFormProps> = ({ onConfirmed, onBack }) => {
    const stripe = useStripe();
    const elements = useElements();
    const [submitting, setSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setSubmitting(true);
        setErrorMessage(null);

        const result = await stripe.confirmPayment({
            elements,
            confirmParams: { return_url: window.location.origin + '/plan?checkout=topup-success' },
            redirect: 'if_required'
        });

        if (result.error) {
            setErrorMessage(result.error.message || 'Payment failed');
            setSubmitting(false);
            return;
        }

        onConfirmed();
    };

    return (
        <form onSubmit={handleSubmit} style={formResetStyle}>
            <PaymentElement />
            {errorMessage && <ErrorBox message={errorMessage} />}
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px', marginTop: '20px' }}>
                <Button variant="link" onClick={onBack} isDisabled={submitting} style={{ color: colors.subtle, fontWeight: 600 }}>
                    Back
                </Button>
                <GradientButton type="submit" isDisabled={!stripe || !elements || submitting} isLoading={submitting}>
                    {submitting ? 'Processing…' : 'Pay now'}
                </GradientButton>
            </div>
        </form>
    );
};
